import { useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "../lib/firebase";
import { useSession } from "../auth/AuthProvider";
import { localized } from "../lib/format";
import { useI18n } from "../i18n";

interface ChatThread {
  id: string;
  hallName: Record<string, string>;
  customerUid: string;
  lastMessage: string;
  lastSenderUid?: string;
}

interface Message {
  id: string;
  senderUid: string;
  text: string;
}

/** Hall-side chat desk: realtime threads, realtime replies (docs/03 chats). */
export function ChatsPage() {
  const { orgId } = useSession();
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const { t, dir } = useI18n();

  useEffect(() => {
    if (!orgId) return;
    const q = query(
      collection(db, "chats"),
      where("orgId", "==", orgId),
      orderBy("lastMessageAt", "desc"),
    );
    return onSnapshot(q, (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<ChatThread, "id">) }));
      setThreads(items);
      setSelected((current) => current ?? items[0]?.id ?? null);
    });
  }, [orgId]);

  useEffect(() => {
    if (!selected) return;
    const q = query(collection(db, "chats", selected, "messages"), orderBy("sentAt"));
    return onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Message, "id">) })));
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    });
  }, [selected]);

  async function send() {
    const uid = auth.currentUser?.uid;
    if (!uid || !selected || !draft.trim()) return;
    const text = draft.trim();
    setDraft("");
    await addDoc(collection(db, "chats", selected, "messages"), {
      senderUid: uid,
      text,
      sentAt: serverTimestamp(),
    });
    await updateDoc(doc(db, "chats", selected), {
      lastMessage: text,
      lastSenderUid: uid,
      lastMessageAt: serverTimestamp(),
    });
  }

  const myUid = auth.currentUser?.uid;

  return (
    <>
      <Typography variant="h5" mb={2}>
        {t("chats_title")}
      </Typography>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ height: "70vh" }}>
        <Paper variant="outlined" sx={{ width: { md: 280 }, overflowY: "auto" }}>
          <List dense>
            {threads.map((thread) => (
              <ListItemButton
                key={thread.id}
                selected={thread.id === selected}
                onClick={() => setSelected(thread.id)}
              >
                <ListItemText
                  primary={localized(thread.hallName)}
                  secondary={thread.lastMessage}
                  secondaryTypographyProps={{ noWrap: true, dir: "auto" }}
                />
              </ListItemButton>
            ))}
            {threads.length === 0 && (
              <Typography color="text.secondary" textAlign="center" p={3}>
                {t("chats_empty")}
              </Typography>
            )}
          </List>
        </Paper>

        <Paper variant="outlined" sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
            {messages.map((message) => {
              const mine = message.senderUid === myUid;
              return (
                <Box
                  key={message.id}
                  display="flex"
                  justifyContent={mine ? "flex-start" : "flex-end"}
                  mb={1}
                >
                  <Box
                    sx={{
                      bgcolor: mine ? "primary.main" : "grey.200",
                      color: mine ? "primary.contrastText" : "text.primary",
                      borderRadius: 3,
                      px: 2,
                      py: 1,
                      maxWidth: "70%",
                    }}
                  >
                    <Typography variant="body2" dir="auto">{message.text}</Typography>
                  </Box>
                </Box>
              );
            })}
            <div ref={bottomRef} />
          </Box>
          {selected && (
            <Stack direction="row" spacing={1} p={1.5} borderTop={1} borderColor="divider">
              <TextField
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void send();
                  }
                }}
                placeholder={t("chats_reply_placeholder")}
                size="small"
                fullWidth
                multiline
                maxRows={3}
              />
              <IconButton color="primary" onClick={send} disabled={!draft.trim()} aria-label={t("common_send")}>
                {/* The glyph points right; mirror it only in RTL so it always points "forward". */}
                <SendIcon sx={{ transform: dir === "rtl" ? "scaleX(-1)" : "none" }} />
              </IconButton>
            </Stack>
          )}
        </Paper>
      </Stack>
    </>
  );
}
